import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./context/CartContext";
import { apiUrl } from "../config/config";
import image from "../assets/33_360x.webp";
import SEO from "./SEO";
import CartSkeleton from "./CartSkeleton";

// -----------------------------------------------------
// SAFE: getUserOrGuestId SHOULD NOT USE ANY HOOK
// -----------------------------------------------------
const getUserOrGuestId = () => {
  const user = localStorage.getItem("user");

  // Logged-in user
  if (user) {
    try {
      const parsed = JSON.parse(user);
      if (parsed?.id) {
        return { user_id: parsed.id, guest_id: null };
      }
    } catch {}
  }

  // Guest user
  let guest_id = localStorage.getItem("guest_id");
  if (!guest_id) {
    guest_id = "guest_" + Math.random().toString(36).substring(2, 12);
    localStorage.setItem("guest_id", guest_id);
  }

  return { user_id: null, guest_id };
};

export default function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { refreshCart } = useCart();

  // -----------------------------------------------------
  // Fetch Cart Items
  // -----------------------------------------------------
  useEffect(() => {
    const { user_id, guest_id } = getUserOrGuestId();

    let url = user_id
      ? `${apiUrl}/api/cart/user/${user_id}`
      : `${apiUrl}/api/cart?guest_id=${guest_id}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCartItems(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Cart fetch error:", err);
        setLoading(false);
      });
  }, []);

  // -----------------------------------------------------
  // Delete Item
  // -----------------------------------------------------
  const deleteItem = async (id) => {
    const { user_id, guest_id } = getUserOrGuestId();

    await fetch(`${apiUrl}/api/cart/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id,
        guest_id,
      }),
    });

    setCartItems((prev) => prev.filter((item) => item.id !== id));

    refreshCart();
  };

  // -----------------------------------------------------
  // Total Price
  // -----------------------------------------------------
  const total = cartItems.reduce((sum, item) => sum + Number(item.price), 0);

  if (loading) return <CartSkeleton />;

  // -----------------------------------------------------
  // EMPTY CART VIEW
  // -----------------------------------------------------
  if (cartItems.length === 0) {
    return (
      <>
        <SEO
          title="Cart"
          description="Personalize Patches For Hats"
          keywords="Personalize Patches For Hats"
        />

        <div className="min-h-[70vh] bg-lightPastelPurple px-4 py-16">
          <div className="relative mx-auto max-w-3xl overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-14 text-center shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:px-10">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative">
              <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-5xl">
                Your Cart is Empty
              </h1>

              <div className="mx-auto mt-4 flex w-full max-w-[230px] items-center justify-center gap-3">
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-keyLime shadow-[0_0_0_5px_rgba(215,255,79,0.25)]"></span>
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
              </div>

              <p className="mx-auto mt-6 max-w-xl text-base font-medium leading-8 text-[#5f5870] md:text-lg">
                Looks like you haven’t added anything yet.
              </p>

              <a
                href="/"
                className="mt-8 inline-flex rounded-2xl bg-keyLime px-7 py-3.5 text-sm font-extrabold text-darkPurple shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531]"
              >
                Continue Shopping
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  // -----------------------------------------------------
  // MAIN CART LAYOUT
  // -----------------------------------------------------
  return (
    <>
      <SEO
        title="Cart"
        description="Personalize Patches For Hats"
        keywords="Personalize Patches For Hats"
      />

      <div className="min-h-screen bg-lightPastelPurple px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* PAGE HEADER */}
          <div className="relative mb-10 overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-10 text-center shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:px-10">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative">
              <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-5xl">
                Shopping Cart
              </h1>

              <div className="mx-auto mt-4 flex w-full max-w-[230px] items-center justify-center gap-3">
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-keyLime shadow-[0_0_0_5px_rgba(215,255,79,0.25)]"></span>
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* LEFT SIDE */}
            <div className="lg:col-span-2">
              <div className="overflow-hidden rounded-[30px] border border-pastelPurple bg-white shadow-[0_18px_45px_rgba(47,34,84,0.08)]">
                {/* HEADER */}
                <div className="hidden grid-cols-12 border-b border-pastelPurple bg-darkPurple px-6 py-4 text-sm font-extrabold text-white md:grid">
                  <div className="col-span-7">Product</div>
                  <div className="col-span-3 text-center">Quantity</div>
                  <div className="col-span-2 text-right">Total</div>
                </div>

                {/* FREE PROOF */}
                <div className="flex flex-col gap-4 border-b border-pastelPurple bg-lightPastelPurple/70 px-4 py-6 md:grid md:grid-cols-12 md:px-6">
                  <div className="flex gap-4 md:col-span-7">
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border border-pastelPurple bg-white p-2 shadow-sm">
                      <img
                        src={image}
                        className="h-full w-full object-contain"
                        alt=""
                      />
                    </div>

                    <h3 className="text-base font-extrabold leading-7 text-darkPurple md:text-lg">
                      Free Proof Approval Before Production
                    </h3>
                  </div>

                  <div className="flex justify-between font-bold text-darkPurple md:col-span-3 md:justify-center">
                    <span className="md:hidden">Qty:</span> 1
                  </div>

                  <div className="flex justify-between font-extrabold text-darkPurple md:col-span-2 md:justify-end">
                    <span className="md:hidden">Total:</span> $0.00
                  </div>
                </div>

                {/* CART ITEMS */}
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col gap-5 border-b border-pastelPurple px-4 py-6 last:border-b-0 md:grid md:grid-cols-12 md:px-6"
                  >
                    {/* PRODUCT */}
                    <div className="flex gap-4 md:col-span-7">
                      <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-pastelPurple bg-lightPastelPurple p-2 shadow-sm">
                        <img
                          src={
                            item.upload_image
                              ? `${apiUrl}/storage/${item.upload_image}`
                              : "/img/no-image.png"
                          }
                          className="h-full w-full rounded-xl object-cover"
                          alt={item.product_name}
                        />
                      </div>

                      <div>
                        <h3 className="text-base font-extrabold leading-7 text-darkPurple md:text-lg">
                          {item.product_name}
                        </h3>

                        <div className="mt-3 space-y-1.5 text-sm font-medium leading-6 text-[#5f5870]">
                          {item?.width && item?.height && (
                            <>
                              <p>
                                <strong className="text-darkPurple">
                                  Width:
                                </strong>{" "}
                                {item.width}"
                              </p>
                              <p>
                                <strong className="text-darkPurple">
                                  Height:
                                </strong>{" "}
                                {item.height}"
                              </p>
                            </>
                          )}

                          {item.selected_filters &&
                            Object.entries(item.selected_filters).map(
                              ([lbl, val]) => (
                                <p key={lbl}>
                                  <strong className="text-darkPurple">
                                    {lbl}:
                                  </strong>{" "}
                                  {val}
                                </p>
                              )
                            )}

                          {item.inputs?.border_thread_color && (
                            <p>
                              <strong className="text-darkPurple">
                                Thread Color:
                              </strong>{" "}
                              {item.inputs.border_thread_color}
                            </p>
                          )}

                          {item.inputs?.special_notes && (
                            <p>
                              <strong className="text-darkPurple">
                                Notes:
                              </strong>{" "}
                              {item.inputs.special_notes}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* QTY + DELETE */}
                    <div className="flex items-center justify-between md:col-span-3 md:justify-center">
                      <span className="font-bold text-darkPurple md:hidden">
                        Qty:
                      </span>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => deleteItem(item.id)}
                          className="flex h-10 w-10 items-center justify-center rounded-xl border border-pastelPurple bg-lightPastelPurple text-base transition hover:bg-white hover:shadow-sm"
                        >
                          🗑
                        </button>

                        <div className="flex h-10 min-w-[48px] items-center justify-center rounded-xl border border-pastelPurple bg-white px-4 text-sm font-extrabold text-darkPurple shadow-sm">
                          {item.qty}
                        </div>
                      </div>
                    </div>

                    {/* TOTAL */}
                    <div className="flex items-center justify-between md:col-span-2 md:justify-end">
                      <span className="font-bold text-darkPurple md:hidden">
                        Total:
                      </span>

                      <div className="text-lg font-extrabold text-darkPurple">
                        ${item.price}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT SIDE SUMMARY */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)]">
                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-keyLime/20 blur-3xl"></div>

                <div className="relative">
                  <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-themePurple">
                    Total
                  </p>

                  <p className="mt-3 text-4xl font-extrabold text-darkPurple">
                    ${total.toFixed(2)}
                  </p>

                  <p className="mt-4 text-sm font-medium leading-7 text-[#5f5870]">
                    Taxes and shipping calculated at checkout.
                  </p>

                  <button
                    onClick={() => navigate("/checkout")}
                    disabled={cartItems.length === 0}
                    className={`mt-6 w-full rounded-2xl px-7 py-3.5 text-center text-sm font-extrabold shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 ${
                      cartItems.length === 0
                        ? "cursor-not-allowed bg-gray-300 text-gray-600"
                        : "bg-keyLime text-darkPurple hover:-translate-y-0.5 hover:bg-[#c8f531]"
                    }`}
                  >
                    Checkout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}